<script setup>
import { ref, computed, watch } from 'vue';
import Chart from 'primevue/chart';
import Dialog from 'primevue/dialog';
import { chartColors, donutChartOptions } from '@/services/constant-services';

const props = defineProps({
  show: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  data: { type: Object, default: null }
});
const emit = defineEmits(['close', 'update:show']);

const selectedTab = ref('allocation');

const allocation = computed(() => props.data?.goals?.[0]?.assetAllocation || []);
const summary = computed(() => props.data?.summary || {});
const goalInfo = computed(() => props.data?.goals?.[0] || {});
const hasError = computed(() => !!goalInfo.value?.error);
const errorMessage = computed(() => goalInfo.value?.error || 'Unable to generate allocation');

const donutData = computed(() => {
  const labels = allocation.value.map(a => a.assetClass);
  const data = allocation.value.map(a => {
    const raw = (a.allocationPercentage || '').toString();
    const num = parseFloat(raw.replace('%', '')) || 0;
    return Number(num.toFixed(2));
  });
  const backgroundColor = allocation.value.map((_, i) => chartColors[i % chartColors.length]);

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor,
        hoverOffset: 8
      }
    ]
  };
});

const donutOptions = { ...donutChartOptions, cutout: '65%' };

// Bell curve distribution data
const bellCurveData = computed(() => {
  const metrics = goalInfo.value?.portfolioMetrics;
  if (!metrics?.p5Corpus || !metrics?.p50Corpus || !metrics?.p95Corpus) return null;

  // Extract numeric values from formatted strings (e.g., "₹1,50,81,066" -> 15081066)
  const parseAmount = (str) => {
    if (!str) return 0;
    return parseFloat(String(str).replace(/[₹,]/g, '')) || 0;
  };

  const p5 = parseAmount(metrics.p5Corpus);
  const p50 = parseAmount(metrics.p50Corpus);
  const p95 = parseAmount(metrics.p95Corpus);
  const targetAmount = parseAmount(goalInfo.value?.projection?.targetAmount);

  // Estimate standard deviation from percentiles
  // In normal distribution: p95 ≈ mean + 1.645*σ, p5 ≈ mean - 1.645*σ
  const stdDev = (p95 - p5) / (2 * 1.645);
  const mean = p50;

  // Generate bell curve points
  const points = [];
  const numPoints = 100;
  const rangeStart = mean - 3 * stdDev;
  const rangeEnd = mean + 3 * stdDev;
  const step = (rangeEnd - rangeStart) / numPoints;

  // Normal distribution formula
  const normalPDF = (x, mu, sigma) => {
    return (1 / (sigma * Math.sqrt(2 * Math.PI))) *
           Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2));
  };

  for (let i = 0; i <= numPoints; i++) {
    const x = rangeStart + i * step;
    const y = normalPDF(x, mean, stdDev);
    points.push({ x, y });
  }

  // Create chart data
  const labels = points.map(() => '');
  const data = points.map(p => p.y);
  const xValues = points.map(p => p.x);

  return {
    labels,
    datasets: [
      {
        label: 'Probability Distribution',
        data: data.map((y, i) => ({ x: xValues[i], y })),
        borderColor: chartColors[0],
        backgroundColor: `${chartColors[0]}40`,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      }
    ],
    markers: { p5, p50, p95, target: targetAmount, mean, stdDev, min: rangeStart, max: rangeEnd }
  };
});

const bellCurveOptions = computed(() => {
  if (!bellCurveData.value) return {};

  const { min, max, p5, p50, p95, target } = bellCurveData.value.markers;

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false
      },
      annotation: {
        annotations: {
          p5Line: {
            type: 'line',
            scaleID: 'x',
            value: p5,
            borderColor: 'rgba(239, 68, 68, 0.7)',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              display: true,
              content: 'Stress',
              position: 'start',
              backgroundColor: 'rgba(239, 68, 68, 0.8)',
              color: '#ffffff',
              font: {
                size: 11,
                weight: 'bold'
              },
              padding: 4
            }
          },
          p50Line: {
            type: 'line',
            scaleID: 'x',
            value: p50,
            borderColor: '#667eea',
            borderWidth: 3,
            label: {
              display: true,
              content: 'Median',
              position: 'end',
              backgroundColor: '#667eea',
              color: '#ffffff',
              font: {
                size: 11,
                weight: 'bold'
              },
              padding: 4
            }
          },
          p95Line: {
            type: 'line',
            scaleID: 'x',
            value: p95,
            borderColor: 'rgba(34, 197, 94, 0.7)',
            borderWidth: 2,
            borderDash: [5, 5],
            label: {
              display: true,
              content: 'Optimistic',
              position: 'start',
              backgroundColor: 'rgba(34, 197, 94, 0.8)',
              color: '#ffffff',
              font: {
                size: 11,
                weight: 'bold'
              },
              padding: 4
            }
          },
          targetLine: target ? {
            type: 'line',
            scaleID: 'x',
            value: target,
            borderColor: 'rgba(59, 130, 246, 0.8)',
            borderWidth: 3,
            label: {
              display: true,
              content: 'Target',
              position: 'end',
              backgroundColor: 'rgba(59, 130, 246, 0.8)',
              color: '#ffffff',
              font: {
                size: 11,
                weight: 'bold'
              },
              padding: 4
            }
          } : undefined
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        min,
        max,
        ticks: {
          callback: function(value) {
            // Format large numbers
            if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
            if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
            return `₹${(value / 1000).toFixed(0)}K`;
          },
          maxTicksLimit: 6
        },
        grid: { display: false }
      },
      y: {
        display: false,
        grid: { display: false }
      }
    }
  };
});

watch(() => props.show, (v) => {
  if (!v) selectedTab.value = 'allocation';
});

const close = () => {
  selectedTab.value = 'allocation';
  emit('update:show', false);
  emit('close');
};

// Calculate progress bar fill percentage
const progressBarFillPercentage = computed(() => {
  const toleranceStr = goalInfo.value?.projection?.['toleranceTarget(95%)'] || '';
  const targetStr = goalInfo.value?.projection?.targetAmount || '';
  const projectedStr = goalInfo.value?.projection?.projectedValue || '';

  // Parse amounts
  const parseAmount = (str) => parseFloat(String(str).replace(/[₹,]/g, '')) || 0;

  const tolerance = parseAmount(toleranceStr);
  const target = parseAmount(targetStr);
  const projected = parseAmount(projectedStr);

  if (tolerance === 0 || target === 0) return 50; // Default fallback

  // Calculate where the bar should end (projected value position in the scale)
  // The scale goes from tolerance to a max value that includes buffer beyond projected
  const surplus = Math.max(0, projected - target);
  const buffer = surplus > 0 ? surplus * 0.3 : (target - tolerance) * 0.1; // 30% of surplus or 10% of range
  const maxValue = projected + buffer;
  const range = maxValue - tolerance;

  if (range === 0) return 50; // Avoid division by zero

  // Calculate fill percentage: how far projected is from tolerance in the total range
  const fillPercentage = ((projected - tolerance) / range) * 100;

  // Clamp between 5% and 98% for visual consistency
  return Math.min(Math.max(fillPercentage, 5), 98);
});

// Calculate the display range for the progress bar labels
const progressBarMaxValue = computed(() => {
  const toleranceStr = goalInfo.value?.projection?.['toleranceTarget(95%)'] || '';
  const targetStr = goalInfo.value?.projection?.targetAmount || '';
  const projectedStr = goalInfo.value?.projection?.projectedValue || '';

  const parseAmount = (str) => parseFloat(String(str).replace(/[₹,]/g, '')) || 0;
  const formatAmount = (num) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
    return `₹${(num / 1000).toFixed(0)}K`;
  };

  const tolerance = parseAmount(toleranceStr);
  const target = parseAmount(targetStr);
  const projected = parseAmount(projectedStr);

  const surplus = Math.max(0, projected - target);
  const buffer = surplus > 0 ? surplus * 0.3 : (target - tolerance) * 0.1;
  const maxValue = projected + buffer;

  return formatAmount(maxValue);
});

// Calculate marker position for vertical lines on bell curve
const calculateMarkerPosition = (value, min, max) => {
  if (max === min) return 50;
  const percentage = ((value - min) / (max - min)) * 100;
  return Math.min(Math.max(percentage, 0), 100);
};
</script>

<template>
  <Dialog
    :visible="props.show"
    @update:visible="(val) => emit('update:show', val)"
    modal
    :closable="true"
    :draggable="false"
    :style="{ width: '90vw', maxWidth: '1200px' }"
    class="goal-allocation-dialog"
    @hide="close"
  >
    <template #header>
      <div class="gaa-header-content">
        <div class="gaa-header-row">
          <!-- Goal Info -->
          <div class="gaa-compact-card goal-card">
            <div class="gaa-goal-icon">
              <i class="pi pi-flag-fill"></i>
            </div>
            <div class="card-details">
              <div class="card-label">Goal</div>
              <div class="card-value">{{ goalInfo.goalName || 'Goal' }}</div>
            </div>
          </div>

          <!-- Target -->
          <div class="gaa-compact-card">
            <div class="card-details">
              <div class="card-label">Target</div>
              <div class="card-value">{{ goalInfo.goalDetails?.targetAmount || summary.totalGoalAmount || '-' }}</div>
            </div>
          </div>

          <!-- Duration -->
          <div class="gaa-compact-card">
            <div class="card-details">
              <div class="card-label">Duration</div>
              <div class="card-value">{{ goalInfo.goalDetails?.timeHorizonText || (goalInfo.goalDetails?.timeHorizon ? goalInfo.goalDetails.timeHorizon + ' years' : '-') }}</div>
            </div>
          </div>

          <!-- Monthly SIP -->
          <div class="gaa-compact-card">
            <div class="stat-icon bg-blue">
              <i class="pi pi-wallet"></i>
            </div>
            <div class="card-details">
              <div class="card-label">Monthly SIP</div>
              <div class="card-value">{{ summary.totalMonthlyContributionNeeded || goalInfo?.investmentPlan?.monthlyContribution || '-' }}</div>
            </div>
          </div>

          <!-- Status -->
          <div class="gaa-compact-card" :title="goalInfo?.projection?.achievableTooltip || ''">
            <div class="stat-icon" :class="(goalInfo?.projection?.achievable === 'Yes' || goalInfo?.projection?.achievable === true) ? 'bg-green' : 'bg-orange'">
              <i :class="(goalInfo?.projection?.achievable === 'Yes' || goalInfo?.projection?.achievable === true) ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'"></i>
            </div>
            <div class="card-details">
              <div class="card-label">Status</div>
              <div class="card-value">{{ (goalInfo?.projection?.achievable === 'Yes' || goalInfo?.projection?.achievable === true) ? 'Achievable' : 'Not Achievable' }}</div>
            </div>
          </div>

          <!-- Success Rate -->
          <div class="gaa-compact-card" :title="goalInfo?.portfolioMetrics?.probabilityOfSuccessTooltip || ''">
            <div class="stat-icon bg-purple">
              <i class="pi pi-chart-line"></i>
            </div>
            <div class="card-details">
              <div class="card-label">Success Rate</div>
              <div class="card-value">{{ goalInfo?.portfolioMetrics?.probabilityOfSuccess || goalInfo?.projection?.probabilityOfSuccess || summary.successPercentage || '-' }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <nav class="gaa-tabs">
      <button :class="['gaa-tab', { active: selectedTab === 'allocation' }]" @click="selectedTab = 'allocation'">Allocation</button>
      <button :class="['gaa-tab', { active: selectedTab === 'projection' }]" @click="selectedTab = 'projection'">Projection</button>
      <button :class="['gaa-tab', { active: selectedTab === 'success' }]" @click="selectedTab = 'success'">Success & Risk</button>
      <button :class="['gaa-tab', { active: selectedTab === 'assumptions' }]" @click="selectedTab = 'assumptions'">Assumptions</button>
    </nav>

    <section class="gaa-content">
      <div v-if="props.loading" class="gaa-loading">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
        <div>Loading allocation, please wait...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="gaa-error-state">
        <div class="error-card">
          <i class="pi pi-exclamation-triangle" style="font-size: 3rem; color: var(--orange-500); margin-bottom: 16px;"></i>
          <h3 class="error-title">Unable to Generate Allocation</h3>
          <p class="error-message">{{ errorMessage }}</p>
          <div class="error-details">
            <h4>Possible reasons:</h4>
            <ul>
              <li v-if="goalInfo?.goalDetails?.timeHorizon === 0 || goalInfo?.goalDetails?.timeHorizon === '0'">
                <strong>Time horizon is 0 years</strong> - Please set a valid goal duration (at least 1 year)
              </li>
              <li v-else-if="!goalInfo?.goalDetails?.timeHorizon">
                <strong>Missing time horizon</strong> - Goal duration is required for allocation
              </li>
              <li>The goal constraints are too restrictive for optimization</li>
              <li>The target amount may be too low or too high for the given time frame</li>
              <li>Risk tolerance settings may need adjustment</li>
            </ul>
          </div>
          <div class="error-actions">
            <p class="suggestion">Try adjusting your goal parameters:</p>
            <ul class="suggestion-list">
              <li>Set a realistic time horizon (minimum 1 year recommended)</li>
              <li>Review target amount and monthly SIP requirements</li>
              <li>Adjust risk tolerance if needed</li>
            </ul>
          </div>
        </div>
      </div>

      <div v-else>
          <!-- Allocation Tab -->
          <div v-if="selectedTab === 'allocation'" class="gaa-tab-content">
            <h4 class="internal-header">Recommended Asset Allocation </h4>
            <p class="subtext">Optimized for your goal and Moderate risk profile.</p>

            <div class="gaa-allocation-grid">
              <div class="gaa-donut">
                <Chart v-if="donutData.labels && donutData.labels.length > 0" type="doughnut" :data="donutData" :options="donutOptions" />
                <div v-else class="gaa-no-data">No allocation data</div>

                <div class="gaa-donut-legend">
                  <div v-for="(a, idx) in allocation" :key="idx" class="legend-row">
                    <span class="legend-color" :style="{ backgroundColor: chartColors[idx % chartColors.length] }"></span>
                    <span class="legend-label">{{ a.assetClass }}</span>
                    <span class="legend-value">{{ a.allocationPercentage }}</span>
                  </div>
                </div>
              </div>

              <div class="gaa-cards">
                <div v-for="(a, i) in allocation" :key="i" class="gaa-asset-card">
                  <div class="gaa-asset-row">
                    <div class="gaa-asset-title">{{ a.assetClass }} • {{ a.allocationPercentage }}</div>
                    <div class="gaa-asset-amt">{{ a.allocationAmount || '-' }}</div>
                  </div>
                  <div class="gaa-instrument">
                    <div class="inst-name">{{ a.recommendedInstrument?.name || (a.recommendedInstrument?.type || '') }}</div>
                    <div class="inst-perf">{{ a.recommendedInstrument?.performance || '' }}</div>
                    <div class="inst-desc">{{ a.recommendedInstrument?.description || '' }}</div>
                  </div>
                </div>

                <div class="gaa-explainer" title="The portfolio is limited to 4 asset classes (each ≥5%) to balance diversification, clarity, and rebalancing efficiency. Real estate excluded. Gold added when SIP &gt; ₹20,000. Risk tolerance caps applied (Moderate)">
                  The portfolio is deliberately limited to 4 asset classes (each ≥5%) to balance diversification, clarity, and rebalancing efficiency.
                  <span class="gaa-explainer-note">i</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Projection Tab -->
          <div v-if="selectedTab === 'projection'" class="gaa-tab-content">
            <h4 class="internal-header">Projected Outcome (Average Return Scenario)</h4>
            <p class="subtext">This is not guaranteed. It assumes steady returns with no volatility.</p>

            <div class="gaa-projection-block">
              <div class="big-number">{{ goalInfo?.projection?.projectedValue || '-' }}</div>
              <div class="projection-detail">
                <div>Target: {{ goalInfo?.projection?.targetAmount || '-' }}</div>
                <div>Minimum acceptable (95%): {{ goalInfo?.projection?.['toleranceTarget(95%)'] || '-' }}</div>
                <div>Surplus over target: {{ goalInfo?.projection?.surplus || '-' }}</div>
              </div>

              <div class="gaa-progbar">
                <div class="prog-bar-visual">
                  <div class="prog-fill" :style="{ width: progressBarFillPercentage + '%' }">
                    <div class="prog-marker" :title="'Projected: ' + (goalInfo?.projection?.projectedValue || '')"></div>
                  </div>
                </div>
                <div class="prog-labels">
                  <div class="label-left">{{ goalInfo?.projection?.['toleranceTarget(95%)'] || 'Tolerance' }}</div>
                  <div class="label-center">{{ goalInfo?.projection?.targetAmount || 'Target' }}</div>
                  <div class="label-projected" :style="{ left: progressBarFillPercentage + '%' }">{{ goalInfo?.projection?.projectedValue || '-' }}</div>
                  <div class="label-right">{{ progressBarMaxValue }}</div>
                </div>
              </div>

              <div class="explain-card">
                <strong>How this number is calculated</strong>
                <p>Monthly SIP of {{ summary.totalMonthlyContributionNeeded || '-' }} is compounded for {{ goalInfo?.goalDetails?.timeHorizon || '-' }} years. Portfolio expected return ({{ goalInfo?.goalDetails?.portfolioExpectedReturn || '-' }}) is applied evenly every month. Volatility is not considered.</p>
                <p class="muted">See next tab to understand how market ups & downs affect this outcome.</p>
              </div>
            </div>
          </div>

          <!-- Success & Risk Tab -->
          <div v-if="selectedTab === 'success'" class="gaa-tab-content">
            <h4 class="internal-header">Success & Risk Overview</h4>
            <div class="gaa-metrics">
              <div class="metric-card">
                <div class="m-label">Success Probability</div>
                <div class="m-value">{{ goalInfo?.portfolioMetrics?.probabilityOfSuccess || '-' }}</div>
                <div class="m-sub">Probability of finishing ≥ {{ goalInfo?.projection?.['toleranceTarget(95%)'] || '-' }}</div>
              </div>
              <div class="metric-card">
                <div class="m-label">Expected Return</div>
                <div class="m-value">{{ goalInfo?.portfolioMetrics?.expectedReturn || '-' }}</div>
              </div>
              <div class="metric-card">
                <div class="m-label">Volatility</div>
                <div class="m-value">{{ goalInfo?.portfolioMetrics?.volatility || '-' }}</div>
              </div>
              <div class="metric-card">
                <div class="m-label">Sharpe (Annual)</div>
                <div class="m-value">{{ goalInfo?.portfolioMetrics?.sharpeRatioAnnual || '-' }}</div>
              </div>
            </div>

            <div class="distribution">
              <div class="dist-legend">
                <span class="legend-item" :title="'A conservative stress outcome. In 5 out of 100 simulated futures, the final corpus may be at or below this level.'">
                  <strong>Stress Scenario:</strong> {{ goalInfo?.portfolioMetrics?.p5Corpus || '-' }}
                </span>
                <span class="legend-separator">|</span>
                <span class="legend-item" :title="'The most typical outcome (median). Half the simulated outcomes end above this, and half below.'">
                  <strong>Median Outcome:</strong> {{ goalInfo?.portfolioMetrics?.p50Corpus || '-' }}
                </span>
                <span class="legend-separator">|</span>
                <span class="legend-item" :title="'A favorable scenario. In strong market conditions, the final corpus could reach or exceed this level.'">
                  <strong>Optimistic Scenario:</strong> {{ goalInfo?.portfolioMetrics?.p95Corpus || '-' }}
                </span>
              </div>
              <div class="bell-visual">
                <div class="chart-container">
                  <Chart
                    v-if="bellCurveData"
                    type="line"
                    :data="bellCurveData"
                    :options="bellCurveOptions"
                    style="height: 200px;"
                  />
                  <div v-else class="no-distribution">Insufficient data for distribution curve</div>

                  <!-- Custom vertical line markers -->
                  <div v-if="bellCurveData" class="vertical-markers">
                    <div
                      class="v-marker stress-marker"
                      :style="{ left: calculateMarkerPosition(bellCurveData.markers.p5, bellCurveData.markers.min, bellCurveData.markers.max) + '%' }"
                      :title="'Stress: ' + (goalInfo?.portfolioMetrics?.p5Corpus || '')"
                    >
                      <div class="marker-label">Stress</div>
                    </div>
                    <div
                      class="v-marker median-marker"
                      :style="{ left: calculateMarkerPosition(bellCurveData.markers.p50, bellCurveData.markers.min, bellCurveData.markers.max) + '%' }"
                      :title="'Median: ' + (goalInfo?.portfolioMetrics?.p50Corpus || '')"
                    >
                      <div class="marker-label">Median</div>
                    </div>
                    <div
                      class="v-marker optimistic-marker"
                      :style="{ left: calculateMarkerPosition(bellCurveData.markers.p95, bellCurveData.markers.min, bellCurveData.markers.max) + '%' }"
                      :title="'Optimistic: ' + (goalInfo?.portfolioMetrics?.p95Corpus || '')"
                    >
                      <div class="marker-label">Optimistic</div>
                    </div>
                    <div
                      v-if="bellCurveData.markers.target"
                      class="v-marker target-marker"
                      :style="{ left: calculateMarkerPosition(bellCurveData.markers.target, bellCurveData.markers.min, bellCurveData.markers.max) + '%' }"
                      :title="'Target: ' + (goalInfo?.projection?.targetAmount || '')"
                    >
                      <div class="marker-label">Target</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="dist-explanation">
                <p class="font-12 text-gray mt-2">
                  This shows the probability distribution of final portfolio values based on Monte Carlo simulations.
                  The curve shows that most outcomes cluster around the median, with 5% chance of values below stress scenario and 5% above optimistic scenario.
                </p>
              </div>
            </div>

            <div class="risk-card">
              <div class="risk-header">Risk Amount: {{ goalInfo?.portfolioMetrics?.riskAmount || '-' }}</div>
              <p>In the worst 5% of market scenarios, the portfolio may end around {{ goalInfo?.portfolioMetrics?.p5Corpus || '-' }}. Compared to the minimum acceptable target of {{ goalInfo?.projection?.['toleranceTarget(95%)'] || '-' }}, this implies a potential shortfall of {{ goalInfo?.portfolioMetrics?.riskAmount || '-' }} in adverse conditions.</p>
              <p>Std deviation: {{ goalInfo?.portfolioMetrics?.riskStdDev || '-' }} (typical variability, not a loss)</p>
              <p class="guidance">Increasing SIP by ~{{ props.data?.goals?.[0]?.recommendations?.additionalMonthlySIPNeeded || '₹1,300' }} /month raises the success probability to ~85%.</p>
            </div>
          </div>

          <!-- Assumptions Tab -->
          <div v-if="selectedTab === 'assumptions'" class="gaa-tab-content">
            <h4 class="internal-header">Methodology</h4>
            <ul>
              <li>Goal-based portfolio optimization</li>
              <li>Exactly 4 assets per optimized portfolio</li>
              <li>Probability-first optimization; validated with Monte Carlo</li>
              <li>1,000 correlated Monte Carlo simulations</li>
            </ul>

            <h4 class="internal-header">Key Assumptions</h4>
            <ul>
              <li>Success ≥ 95% of goal (tolerance logic)</li>
              <li>Monthly SIP = annual / 12</li>
              <li>No taxes, fees, inflation considered</li>
              <li>Returns follow historical mean & correlation patterns</li>
              <li>Negative returns capped at -95% to avoid unrealistic paths</li>
            </ul>
          </div>
        </div>
      </section>
    </Dialog>
</template>

<style scoped>
.goal-allocation-dialog :deep(.p-dialog-header) {
  padding: 12px 12px 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-bottom: none;
  border-radius: 12px 12px 0 0;
}

.goal-allocation-dialog :deep(.p-dialog-header .p-dialog-title) {
  color: #ffffff !important;
}

.goal-allocation-dialog :deep(.p-dialog-header-icons button) {
  width: 56px !important;
  height: 40px !important;
  background: rgba(255, 255, 255, 0.2) !important;
  border-radius: 8px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.goal-allocation-dialog :deep(.p-dialog-header-icons button .p-icon) {
  color: #ffffff !important;
  font-size: 1.5rem !important;
  font-weight: 700 !important;
}

.goal-allocation-dialog :deep(.p-dialog-header-icons button:hover) {
  background: rgba(255, 255, 255, 0.3) !important;
  transform: scale(1.08);
}

.goal-allocation-dialog :deep(.p-dialog-content) {
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
  background: #f8f9fa;
}

.goal-allocation-dialog :deep(.p-dialog) {
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.gaa-header-content {
  width: 98%;
  margin-top: 12px;
  margin-left: 4px;
}

.gaa-header-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: stretch;
}

.gaa-compact-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 6px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
  flex: 1;
  min-width: 110px;
  cursor: help;
}

.gaa-compact-card.goal-card {
  flex: 1.0;
}

.gaa-compact-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.gaa-goal-icon {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.gaa-goal-icon i {
  font-size: 0.85rem;
  color: white;
}

.card-details {
  flex: 1;
  min-width: 0;
}

.card-label {
  font-size: 0.55rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  margin-bottom: 0px;
  line-height: 1;
}

.card-value {
  font-size: 0.75rem;
  color: #212529;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}

.stat-icon {
  width: 24px;
  height: 24px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-icon.bg-blue {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.stat-icon.bg-green {
  background: linear-gradient(135deg, #10b981, #059669);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.stat-icon.bg-orange {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.stat-icon.bg-purple {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.3);
}

.stat-icon i {
  font-size: 0.75rem;
  color: white;
}

.gaa-tabs {
  display: flex;
  gap: 12px;
  padding: 20px 32px;
  border-bottom: 1px solid #e9ecef;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.gaa-tab {
  padding: 12px 24px;
  border-radius: 8px;
  border: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.gaa-tab::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transform: translateX(-50%);
  transition: width 0.3s ease;
}

.gaa-tab:hover {
  background: rgba(102, 126, 234, 0.08);
  color: #667eea;
  transform: translateY(-2px);
}

.gaa-tab.active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
  border-color: #667eea;
  color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.gaa-tab.active::before {
  width: 100%;
}

.gaa-content {
  padding-top: 1px;
  padding-right: 16px;
  padding-bottom: 0px;
  padding-left: 16px;
  background: #f8f9fa;
}

.gaa-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  gap: 20px;
  color: #667eea;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.gaa-loading i {
  color: #667eea;
}

.gaa-tab-content {
  min-height: 400px;
  background: white;
  border-radius: 12px;
  padding-top: 0px;
  padding-right: 16px;
  padding-bottom: 0px;
  padding-left: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.internal-header {
  font-size: 1.4rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  letter-spacing: -0.5px;
}

.subtext {
  font-size: 0.95rem;
  color: #6c757d;
  margin-bottom: 24px;
  line-height: 1.6;
}

.gaa-allocation-grid {
  display: flex;
  gap: 20px;
  margin-top: 20px;
}

.gaa-donut {
  width: 40%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
}

.gaa-donut-legend {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.legend-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
}

.legend-color {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  color: var(--text-color);
}

.legend-value {
  font-weight: 600;
  color: var(--text-color);
}

.gaa-cards {
  width: 60%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gaa-asset-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e9ecef;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.gaa-asset-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #667eea, #764ba2);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.gaa-asset-card:hover {
  transform: translateX(4px);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.12);
  border-color: #667eea;
}

.gaa-asset-card:hover::before {
  transform: scaleY(1);
}

.gaa-asset-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.gaa-asset-title {
  font-weight: 700;
  color: #212529;
  font-size: 1rem;
  letter-spacing: -0.3px;
}

.gaa-asset-amt {
  font-weight: 800;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.1rem;
}

.gaa-instrument {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--surface-border);
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.inst-name {
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: 4px;
}

.inst-perf {
  margin-bottom: 4px;
}

.gaa-explainer {
  margin-top: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  border-radius: 12px;
  font-size: 0.9rem;
  color: #495057;
  border-left: 4px solid #667eea;
  line-height: 1.7;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.gaa-projection-block {
  text-align: center;
  padding: 20px;
}

.big-number {
  font-size: 3rem;
  font-weight: 900;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 24px;
  letter-spacing: -1px;
  text-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.projection-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 1rem;
  color: #495057;
  margin-bottom: 32px;
  font-weight: 500;
}

.projection-detail > div {
  padding: 10px 16px;
  background: rgba(102, 126, 234, 0.06);
  border-radius: 8px;
  border-left: 3px solid #667eea;
}

.gaa-progbar {
  margin: 24px 0;
  position: relative;
}

.prog-bar-visual {
  height: 14px;
  background: #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #dee2e6;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;
}

.prog-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 600;
  position: relative;
}

.label-left,
.label-center,
.label-right {
  flex-shrink: 0;
}

.label-projected {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
  color: #667eea;
  font-weight: 700;
  font-size: 0.9rem;
}

.prog-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  position: relative;
}

.prog-marker {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  background: #ffffff;
  border-radius: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.explain-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px 24px;
  border-radius: 12px;
  text-align: left;
  margin-top: 32px;
  border: 2px solid #dee2e6;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.explain-card strong {
  display: block;
  margin-bottom: 12px;
  color: #212529;
  font-weight: 700;
  font-size: 1.05rem;
}

.explain-card p {
  margin: 10px 0;
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.7;
}

.muted {
  font-style: italic;
  opacity: 0.7;
  color: #6c757d;
}

.gaa-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
}

.metric-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #e9ecef;
  padding: 24px;
  border-radius: 16px;
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(102, 126, 234, 0.15);
  border-color: #667eea;
}

.metric-card:hover::before {
  transform: scaleX(1);
}

.distribution {
  margin: 32px 0;
  padding: 28px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  border: 2px solid #e9ecef;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.dist-legend {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 20px;
  font-weight: 500;
  padding: 16px 20px;
  background: rgba(102, 126, 234, 0.08);
  border-radius: 8px;
  border-left: 4px solid #667eea;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.legend-item {
  cursor: help;
  transition: all 0.2s ease;
  padding: 4px 8px;
  border-radius: 4px;
}

.legend-item:hover {
  background: rgba(102, 126, 234, 0.15);
  transform: translateY(-1px);
}

.legend-item strong {
  color: #667eea;
  font-weight: 600;
  margin-right: 4px;
}

.legend-separator {
  color: #dee2e6;
  font-weight: 300;
  margin: 0 4px;
}

.bell-visual {
  margin-top: 16px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.chart-container {
  position: relative;
}

.vertical-markers {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  padding: 0 24px 50px 44px;
}

.v-marker {
  position: absolute;
  top: 0;
  bottom: 30px;
  width: 2px;
  pointer-events: auto;
  cursor: help;
}

.stress-marker {
  background: linear-gradient(to bottom, rgba(239, 68, 68, 0.7) 10%, rgba(239, 68, 68, 0.7) 90%);
  border-left: 2px dashed rgba(239, 68, 68, 0.7);
}

.median-marker {
  background: rgba(102, 126, 234, 0.8);
  width: 3px;
}

.optimistic-marker {
  background: linear-gradient(to bottom, rgba(34, 197, 94, 0.7) 10%, rgba(34, 197, 94, 0.7) 90%);
  border-left: 2px dashed rgba(34, 197, 94, 0.7);
}

.target-marker {
  background: rgba(59, 130, 246, 0.8);
  width: 3px;
}

.marker-label {
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(102, 126, 234, 0.9);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: bold;
  white-space: nowrap;
  pointer-events: auto;
}

.stress-marker .marker-label {
  background: rgba(239, 68, 68, 0.9);
}

.optimistic-marker .marker-label {
  background: rgba(34, 197, 94, 0.9);
}

.target-marker .marker-label {
  background: rgba(59, 130, 246, 0.9);
}

.m-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.m-value {
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  line-height: 1;
}

.m-sub {
  line-height: 1.4;
}

.no-distribution {
  text-align: center;
  padding: 40px;
  color: var(--text-color-secondary);
  font-style: italic;
}

.dist-explanation {
  margin-top: 12px;
}

.font-12 {
  font-size: 0.75rem;
}

.text-gray {
  color: #4b5563;
}

.mt-2 {
  margin-top: 8px;
}

.risk-card {
  background: linear-gradient(135deg, #fff5f5 0%, #ffe3e3 100%);
  border: 2px solid #ffc9c9;
  padding: 24px;
  border-radius: 16px;
  margin-top: 24px;
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.1);
}

.risk-header {
  font-weight: 800;
  font-size: 1.3rem;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
  letter-spacing: -0.5px;
}

.risk-card p {
  margin: 8px 0;
  font-size: 0.875rem;
  color: #495057;
  line-height: 1.6;
}

.guidance {
  font-style: normal;
  color: #1971c2;
  background: linear-gradient(135deg, #e7f5ff 0%, #d0ebff 100%);
  padding: 16px 20px;
  border-radius: 12px;
  border-left: 4px solid #1971c2;
  margin-top: 16px;
  font-weight: 600;
  box-shadow: 0 2px 12px rgba(25, 113, 194, 0.1);
}

.gaa-tab-content ul {
  list-style: none;
  padding-left: 0;
}

.gaa-tab-content ul li {
  padding: 8px 0 8px 24px;
  position: relative;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  line-height: 1.6;
}

.gaa-tab-content ul li::before {
  content: '•';
  position: absolute;
  left: 8px;
  color: var(--primary-color);
  font-weight: bold;
}

.gaa-no-data {
  padding: 40px;
  text-align: center;
  color: var(--text-color-secondary);
  font-style: italic;
}

.gaa-error-state {
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-card {
  max-width: 600px;
  text-align: center;
  background: var(--surface-ground);
  padding: 32px;
  border-radius: 12px;
  border: 2px solid var(--orange-200);
}

.error-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 12px;
}

.error-message {
  font-size: 1rem;
  color: var(--orange-600);
  margin-bottom: 24px;
  font-weight: 500;
}

.error-details {
  text-align: left;
  background: var(--surface-card);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  border-left: 4px solid var(--orange-500);
}

.error-details h4 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 12px 0;
}

.error-details ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.error-details li {
  padding: 8px 0 8px 24px;
  position: relative;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  line-height: 1.6;
}

.error-details li::before {
  content: '⚠';
  position: absolute;
  left: 0;
  color: var(--orange-500);
  font-weight: bold;
}

.error-details li strong {
  color: var(--text-color);
}

.error-actions {
  text-align: left;
  background: var(--blue-50);
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid var(--blue-500);
}

.error-actions .suggestion {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 12px 0;
}

.suggestion-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-list li {
  padding: 6px 0 6px 24px;
  position: relative;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  line-height: 1.6;
}

.suggestion-list li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--blue-500);
  font-weight: bold;
}

@media (max-width: 768px) {
  .goal-allocation-dialog :deep(.p-dialog) {
    width: 95vw !important;
  }

  .gaa-header-row {
    flex-direction: column;
  }

  .gaa-compact-card {
    min-width: 100%;
  }

  .gaa-allocation-grid {
    flex-direction: column;
  }

  .gaa-donut,
  .gaa-cards {
    width: 100%;
  }

  .gaa-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
